import { computed, getCurrentInstance, inject, onMounted, onUnmounted, ref, watch } from 'vue';
import { globalValidators } from './validation';

export const useFieldValidation = (modelValueFunc, rulesFunc) => {
	const errorMessage = ref('');

	const formApi = inject('form-component', null);

	const validate = async ({ setErrorMessage }) => {
		let msg = '';
		const modelValue = modelValueFunc();
		const rules = rulesFunc();
		await Promise.all(
			rules.map(async (rule) => {
				if (msg !== '') return;
				if (typeof rule === 'string' && !globalValidators[rule]) {
					console.error('[Validation] Unsupported global validator', rule);
					return;
				}
				const ruleFunction = typeof rule === 'string' ? globalValidators[rule] : rule;
				const ruleRes = await ruleFunction(modelValue);
				if (typeof ruleRes === 'string') {
					msg = ruleRes;
					return;
				}
			}),
		);

		if (setErrorMessage) {
			errorMessage.value = msg;
		}
		return !msg;
	};

	const resetErrorMessage = () => {
		errorMessage.value = '';
	};

	const hasErrorMessage = computed(() => {
		return errorMessage.value !== '';
	});

	watch(modelValueFunc, () => {
		validate({ setErrorMessage: true });
	});

	watch(
		rulesFunc,
		() => {
			validate({ setErrorMessage: true });
		},
		{ deep: true },
	);

	const vm = getCurrentInstance();
	onMounted(() => {
		if (!formApi) return;

		const uid = vm.uid;
		formApi.registerField({
			uid,
			validateFunction: validate,
			vm,
		});
	});

	onUnmounted(() => {
		if (!formApi) return;

		formApi.unregisterField({ uid: vm.uid });
	});

	return {
		errorMessage,
		validate,
		hasErrorMessage,
		resetErrorMessage,
	};
};

export const useValidationGroup = () => {
	const formApi = inject('form-component', null);
	const fields = formApi?.fields?.value;

	const validationInputs = computed(() => {
		return Object.values(fields).map((field) => {
			return {
				invalid: field?.vm?.exposed?.hasErrorMessage?.value,
				key: field?.vm?.attrs?.validationGroup,
			};
		});
	});

	const validationGroups = computed(() => {
		const keys = [...new Set(validationInputs.value.map((input) => input.key))].filter(Boolean);
		return keys.map((key) => {
			return {
				key,
				invalid: validationInputs.value.some((input) => input.key == key && input.invalid),
			};
		});
	});

	const isValidGroup = (key) => {
		return !validationGroups.value.find((group) => group.key == key)?.invalid;
	};

	const isValidGroups = (keys) => {
		return keys.every((key) => isValidGroup(key));
	};

	return {
		validationInputs,
		validationGroups,
		isValidGroup,
		isValidGroups,
	};
};
